/**
 * Main App component for Claude Web UI
 *
 * This is the root component that integrates:
 * - ChatProvider for chat state management
 * - ChatSidebar for channel/DM navigation
 * - ChatPanel for active conversation display
 * - Toggle functionality for chat panel visibility
 *
 * Layout:
 * - Left sidebar: Claude sessions list (placeholder)
 * - Center: Active session/main content area
 * - Right sidebar: Chat panel (collapsible)
 */

import { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import {
  MessageCircle,
  MessageSquarePlus,
  X,
  ChevronRight,
  ChevronLeft,
  Bot,
  Settings,
  User,
  LogOut,
  Home,
  Monitor,
  Loader2,
  Package,
  Users,
  Server,
  Shield,
  Hash,
  FolderKanban,
  ArrowLeft,
  PanelLeftClose,
  PanelLeftOpen,
  ListTodo,
  ExternalLink,
  Plus,
  Star,
  MoreHorizontal,
  Trash2,
  Edit3,
  FolderOpen,
  FileText,
  Sparkles,
  Zap,
  ScrollText,
  Key
} from 'lucide-react';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { ChatProvider, useChat } from './contexts/ChatContext.jsx';
import { MessagesProvider } from './contexts/MessagesContext.jsx';
import { UIProvider, useUI } from './contexts/UIContext.jsx';
import { WorkspaceSessionProvider, useWorkspaceSession } from './contexts/WorkspaceSessionContext.jsx';
import { AgentChatProvider, useAgentChat } from './contexts/AgentChatContext.jsx';
import ChatChannelDropdown from './components/chat/ChatChannelDropdown.jsx';
import ChatWindow from './components/chat/ChatWindow.jsx';
import ChatInput from './components/chat/ChatInput.jsx';
import UnreadBadge from './components/chat/UnreadBadge.jsx';

// Admin Components (lazy imports would be better for production)
import HostManager from './components/admin/HostManager.jsx';
import ChannelManager from './components/admin/ChannelManager.jsx';
import RoleManager from './components/admin/RoleManager.jsx';
import SkillManager from './components/admin/SkillManager.jsx';
import ProjectManager from './components/admin/ProjectManager.jsx';
import UserManager from './components/admin/UserManager.jsx';
import { ClaudeMdManager } from './components/claude-templates/index.js';
import InsightCacheConfig from './components/admin/InsightCacheConfig.jsx';
import WhispererConfig from './components/admin/WhispererConfig.jsx';
import SystemPromptEditor from './components/admin/SystemPromptEditor.jsx';
import CredentialManager from './components/admin/CredentialManager.jsx';

// Session Components
import { SessionsView } from './components/session/SessionsView.jsx';

// Unified Agents Component
import AgentsView from './components/agents/AgentsView.jsx';

// WebSocket Hook
import { useWebSocket } from './hooks/useWebSocket';

// User Components
import UserProjectsView from './components/user/UserProjectsView.jsx';
import UserTasksView from './components/user/UserTasksView.jsx';

// Task Management Components
import { TasksView } from './components/tasks/index.js';

// Common Components
import ConfirmDialog from './components/common/ConfirmDialog.jsx';

// Stores
import useUIStore from './stores/uiStore.js';

// ============================================
// Constants
// ============================================

/**
 * Chat panel width configuration (fixed width with dropdown header)
 */
const CHAT_PANEL_WIDTH = 420;

/**
 * Available views/pages in the app
 */
const VIEWS = {
  DASHBOARD: 'dashboard',
  SESSIONS: 'sessions',
  USER_AGENTS: 'user:agents',
  USER_PROJECTS: 'user:projects',
  USER_TASKS: 'user:tasks',
  USER_TEMPLATES: 'user:templates',
  PROJECT_TASKS: 'project:tasks',
  ADMIN_AGENTS: 'admin:agents',
  ADMIN_HOSTS: 'admin:hosts',
  ADMIN_CHANNELS: 'admin:channels',
  ADMIN_ROLES: 'admin:roles',
  ADMIN_SKILLS: 'admin:skills',
  ADMIN_TEMPLATES: 'admin:templates',
  ADMIN_PROJECTS: 'admin:projects',
  ADMIN_USERS: 'admin:users',
  ADMIN_INSIGHT_CACHE: 'admin:insight-cache',
  ADMIN_WHISPERER: 'admin:whisperer',
  ADMIN_SYSTEM_PROMPTS: 'admin:system-prompts',
  ADMIN_CREDENTIALS: 'admin:credentials'
};

/**
 * Admin navigation items
 */
const ADMIN_NAV_ITEMS = [
  { id: VIEWS.ADMIN_AGENTS, label: 'Agents', icon: Bot, description: 'Manage agent slots and sessions' },
  { id: VIEWS.ADMIN_HOSTS, label: 'Hosts', icon: Server, description: 'Configure host machines' },
  { id: VIEWS.ADMIN_USERS, label: 'Users', icon: Users, description: 'Manage user host access' },
  { id: VIEWS.ADMIN_CHANNELS, label: 'Channels', icon: Hash, description: 'Create and manage channels' },
  { id: VIEWS.ADMIN_ROLES, label: 'Roles', icon: Shield, description: 'Define roles and permissions' },
  { id: VIEWS.ADMIN_SKILLS, label: 'Skills', icon: Package, description: 'Manage agent skills' },
  { id: VIEWS.ADMIN_TEMPLATES, label: 'Templates', icon: FileText, description: 'Manage CLAUDE.md templates' },
  { id: VIEWS.ADMIN_PROJECTS, label: 'Projects', icon: FolderKanban, description: 'Configure projects' },
  { id: VIEWS.PROJECT_TASKS, label: 'Tasks', icon: ListTodo, description: 'View and manage project tasks' },
  { id: VIEWS.ADMIN_INSIGHT_CACHE, label: 'Insight Cache', icon: Sparkles, description: 'Configure agent insight extraction' },
  { id: VIEWS.ADMIN_WHISPERER, label: 'Whisperer', icon: Zap, description: 'Configure reminder rules for agents' },
  { id: VIEWS.ADMIN_SYSTEM_PROMPTS, label: 'System Prompts', icon: ScrollText, description: 'Manage agent system prompt extensions' },
  { id: VIEWS.ADMIN_CREDENTIALS, label: 'Credentials', icon: Key, description: 'Manage credentials and secrets' }
];

// ============================================
// Sub-Components
// ============================================

/**
 * Chat toggle button for the header
 * @param {object} props - Component props
 * @param {boolean} props.isChatOpen - Whether chat panel is open
 * @param {Function} props.onToggle - Toggle callback
 * @param {number} [props.unreadCount] - Total unread count
 * @returns {React.ReactElement} Toggle button
 */
const ChatToggleButton = memo(function ChatToggleButton({
  isChatOpen,
  onToggle,
  unreadCount = 0
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`
        relative flex items-center justify-center
        w-10 h-10 rounded-lg
        transition-all duration-200
        ${isChatOpen
          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
          : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
        }
      `}
      title={isChatOpen ? 'Close chat panel' : 'Open chat panel'}
      aria-label={isChatOpen ? 'Close chat panel' : 'Open chat panel'}
      aria-expanded={isChatOpen}
    >
      <MessageCircle className="w-5 h-5" />
      {!isChatOpen && unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 text-xs font-bold text-white bg-red-500 rounded-full px-1">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
});

/**
 * Open tasks popout window
 */
function openTasksPopout() {
  const popoutUrl = `${window.location.origin}/tasks?popout=true`;
  window.open(
    popoutUrl,
    'task-board-popout',
    'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
  );
}

/**
 * Create Session Modal
 * Allows creating new session or from project
 */
const CreateSessionModal = memo(function CreateSessionModal({ isOpen, onClose, onCreated }) {
  const { createSession, createFromProject, switchSession } = useWorkspaceSession();
  const [mode, setMode] = useState('new'); // 'new' or 'project'
  const [name, setName] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch projects when switching to project mode
  useEffect(() => {
    if (mode === 'project' && projects.length === 0) {
      fetch('/api/projects')
        .then(res => res.json())
        .then(setProjects)
        .catch(err => setError(err.message));
    }
  }, [mode, projects.length]);

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      let newSession;
      if (mode === 'new') {
        if (!name.trim()) {
          setError('Name is required');
          setLoading(false);
          return;
        }
        newSession = await createSession({ name: name.trim() });
      } else {
        if (!selectedProject) {
          setError('Select a project');
          setLoading(false);
          return;
        }
        newSession = await createFromProject(selectedProject.id);
      }
      switchSession(newSession);
      onCreated?.(newSession);
      onClose();
      setName('');
      setSelectedProject(null);
      setMode('new');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h3 className="text-lg font-medium text-white">Create Workspace Session</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* Mode toggle */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setMode('new')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                mode === 'new'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              New Empty
            </button>
            <button
              type="button"
              onClick={() => setMode('project')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                mode === 'project'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              From Project
            </button>
          </div>

          {mode === 'new' ? (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Session Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Workspace"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
                autoFocus
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Project
              </label>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {projects.map(project => (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => setSelectedProject(project)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                      selectedProject?.id === project.id
                        ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <FolderKanban className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{project.name}</span>
                  </button>
                ))}
                {projects.length === 0 && !error && (
                  <div className="text-center py-4 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    <span className="text-sm">Loading projects...</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3 text-sm text-red-400">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
});

/**
 * Workspace Sessions List for sidebar
 * Shows all workspace sessions with switch/create/delete functionality
 */
const WorkspaceSessionsList = memo(function WorkspaceSessionsList({ onViewChange }) {
  const {
    sessions,
    activeSession,
    loading,
    switchSession,
    deleteSession,
    setDefaultSession,
    updateSession
  } = useWorkspaceSession();

  const [menuOpen, setMenuOpen] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [renamingSession, setRenamingSession] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, session: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle rename
  const handleRename = async (sessionId) => {
    if (!renameValue.trim()) return;
    try {
      await updateSession(sessionId, { name: renameValue.trim() });
      setRenamingSession(null);
      setRenameValue('');
    } catch (err) {
      console.error('Failed to rename session:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-2">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
        </div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <>
        <div className="p-2">
          <div className="text-center py-6 text-gray-500">
            <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No sessions yet</p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="mt-2 text-xs text-indigo-400 hover:text-indigo-300"
            >
              Create Session
            </button>
          </div>
        </div>
        <CreateSessionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => onViewChange(VIEWS.SESSIONS)}
        />
      </>
    );
  }

  return (
    <>
      <div className="p-2 space-y-1">
        <div className="flex items-center justify-between px-2 py-1">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            Sessions
          </span>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="p-1 rounded text-gray-500 hover:text-white hover:bg-gray-700"
            title="Create new session"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>

      {sessions.map((session) => {
        const isActive = activeSession?.id === session.id;
        const isMenuOpen = menuOpen === session.id;

        return (
          <div
            key={session.id}
            className="relative group"
          >
            {renamingSession === session.id ? (
              // Inline rename input
              <div className="flex items-center gap-2 px-3 py-2">
                <Monitor className="w-4 h-4 flex-shrink-0 text-gray-400" />
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename(session.id);
                    if (e.key === 'Escape') {
                      setRenamingSession(null);
                      setRenameValue('');
                    }
                  }}
                  onBlur={() => {
                    if (renameValue.trim()) handleRename(session.id);
                    else {
                      setRenamingSession(null);
                      setRenameValue('');
                    }
                  }}
                  autoFocus
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-0.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    switchSession(session);
                    onViewChange(VIEWS.SESSIONS);
                  }}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left
                    ${isActive
                      ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }
                    transition-colors
                  `}
                >
                  <Monitor className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 truncate">{session.name}</span>
                  {session.isDefault && (
                    <Star className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                  )}
                </button>

                {/* Context menu button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(isMenuOpen ? null : session.id);
                  }}
                  className={`
                    absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded
                    text-gray-500 hover:text-white hover:bg-gray-700
                    ${isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                    transition-opacity
                  `}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>

                {/* Dropdown menu */}
                {isMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-40 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      type="button"
                      onClick={() => {
                        setRenameValue(session.name);
                        setRenamingSession(session.id);
                        setMenuOpen(null);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Rename</span>
                    </button>
                    {!session.isDefault && (
                      <button
                        type="button"
                        onClick={() => {
                          setDefaultSession(session.id);
                          setMenuOpen(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                      >
                        <Star className="w-4 h-4" />
                        <span>Set as Default</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteDialog({ isOpen: true, session });
                        setMenuOpen(null);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
      </div>
      <CreateSessionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => onViewChange(VIEWS.SESSIONS)}
      />

      {/* Delete Session Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Session"
        itemName={deleteDialog.session?.name}
        warning="This action cannot be undone."
        onConfirm={async () => {
          if (!deleteDialog.session) return;
          setIsDeleting(true);
          try {
            await deleteSession(deleteDialog.session.id);
            setDeleteDialog({ isOpen: false, session: null });
          } catch (err) {
            console.error('Failed to delete session:', err);
          } finally {
            setIsDeleting(false);
          }
        }}
        onCancel={() => setDeleteDialog({ isOpen: false, session: null })}
        isProcessing={isDeleting}
      />
    </>
  );
});

/**
 * Left sidebar with Claude sessions and admin navigation
 * Supports collapsed mode for more screen space
 * @param {object} props - Component props
 * @param {string} props.currentView - Current active view
 * @param {Function} props.onViewChange - View change callback
 * @param {boolean} props.isCollapsed - Whether sidebar is collapsed
 * @param {Function} props.onToggleCollapse - Toggle collapse callback
 * @returns {React.ReactElement} Left sidebar
 */
const LeftSidebar = memo(function LeftSidebar({
  currentView,
  onViewChange,
  isCollapsed,
  onToggleCollapse
}) {
  const { user, logout, isAuthenticated, authEnabled, isAdmin } = useAuth();
  const { sessions, activeSession, switchSession, loading: sessionsLoading } = useWorkspaceSession();
  const { subscribe } = useWebSocket();
  const [showAdminNav, setShowAdminNav] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [trustCount, setTrustCount] = useState(0);

  const isAdminView = currentView?.startsWith('admin:');

  // Fetch template edit counts for sidebar badges
  const fetchCounts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch('/api/claude-templates/pending-edits?status=pending', {
        credentials: 'include',
      });
      if (!res.ok) return;
      const result = await res.json();
      setPendingCount(result.totalPending || 0);
      setTrustCount(result.totalUnreadChanges || 0);
    } catch {
      // Silent fail — badges just won't show
    }
  }, [isAuthenticated]);

  // Initial fetch + polling
  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Subscribe to WebSocket events for immediate badge updates
  useEffect(() => {
    const cleanupPending = subscribe('template:pending-edit', fetchCounts);
    const cleanupResolved = subscribe('template:edit-resolved', fetchCounts);
    return () => {
      cleanupPending?.();
      cleanupResolved?.();
    };
  }, [subscribe, fetchCounts]);

  // Collapsed sidebar - icon only mode
  if (isCollapsed) {
    return (
      <aside className="flex flex-col w-14 h-full bg-gray-900 border-r border-gray-800 transition-all duration-200">
        {/* Header - logo + expand button */}
        <div className="flex flex-col items-center py-2 border-b border-gray-800 gap-1">
          <button
            type="button"
            onClick={() => onViewChange(VIEWS.DASHBOARD)}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            title="Dashboard"
          >
            <Bot className="w-6 h-6 text-indigo-400" />
          </button>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            title="Expand sidebar"
          >
            <PanelLeftOpen className="w-4 h-4" />
          </button>
        </div>

        {/* Sessions list - icon only */}
        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
          {sessionsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex justify-center py-4">
              <FolderOpen className="w-5 h-5 text-gray-600" title="No sessions" />
            </div>
          ) : (
            sessions.map((session) => {
              const isActive = activeSession?.id === session.id;
              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => {
                    switchSession(session);
                    onViewChange(VIEWS.SESSIONS);
                  }}
                  className={`
                    relative w-full flex items-center justify-center p-2.5 rounded-lg
                    ${isActive
                      ? 'bg-indigo-600/20 text-indigo-400 ring-1 ring-indigo-500/30'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                    }
                    transition-colors
                  `}
                  title={session.name}
                >
                  <Monitor className="w-5 h-5" />
                  {session.isDefault && (
                    <Star className="absolute -right-0.5 -top-0.5 w-2.5 h-2.5 text-yellow-500 fill-yellow-500" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* User Navigation - collapsed */}
        <div className="border-t border-gray-800 px-2 py-2 space-y-1">
          <button
            type="button"
            onClick={() => onViewChange(VIEWS.USER_AGENTS)}
            className={`
              w-full flex items-center justify-center p-2.5 rounded-lg
              ${currentView === VIEWS.USER_AGENTS
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }
              transition-colors
            `}
            title="My Agents"
          >
            <Bot className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => onViewChange(VIEWS.USER_PROJECTS)}
            className={`
              w-full flex items-center justify-center p-2.5 rounded-lg
              ${currentView === VIEWS.USER_PROJECTS
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }
              transition-colors
            `}
            title="My Projects"
          >
            <FolderKanban className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => onViewChange(VIEWS.USER_TASKS)}
            className={`
              w-full flex items-center justify-center p-2.5 rounded-lg
              ${currentView === VIEWS.USER_TASKS
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }
              transition-colors
            `}
            title="My Tasks"
          >
            <ListTodo className="w-5 h-5" />
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => onViewChange(VIEWS.USER_TEMPLATES)}
              className={`
                w-full flex items-center justify-center p-2.5 rounded-lg
                ${currentView === VIEWS.USER_TEMPLATES
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }
                transition-colors
              `}
              title="My Templates"
            >
              <FileText className="w-5 h-5" />
            </button>
            {pendingCount > 0 && (
              <span
                className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white rounded-full px-0.5 bg-orange-500"
                title={`${pendingCount} pending edit${pendingCount !== 1 ? 's' : ''}`}
                aria-label={`${pendingCount} pending edits`}
              >
                {pendingCount}
              </span>
            )}
          </div>
        </div>

        {/* Admin Panel - at bottom, matching expanded layout (only for admins) */}
        {isAdmin && (
          <div className="border-t border-gray-800">
            {/* Admin toggle */}
            <div className="px-2 py-2">
              <button
                type="button"
                onClick={() => setShowAdminNav(!showAdminNav)}
                className={`
                  w-full flex items-center justify-center p-2.5 rounded-lg
                  ${showAdminNav || isAdminView
                    ? 'bg-indigo-600/20 text-indigo-400'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }
                  transition-colors
                `}
                title="Admin Panel"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>

            {/* Admin items when expanded */}
            {showAdminNav && (
              <div className="px-2 pb-2 space-y-1">
                {ADMIN_NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentView === item.id;
                  const isTasksItem = item.id === VIEWS.PROJECT_TASKS;
                  return (
                    <div key={item.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => onViewChange(item.id)}
                        className={`
                          w-full flex items-center justify-center p-2.5 rounded-lg
                          ${isActive
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                          }
                          transition-colors
                        `}
                        title={item.label}
                      >
                        <Icon className="w-5 h-5" />
                      </button>
                      {/* Popout button for Tasks - shown on hover */}
                      {isTasksItem && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openTasksPopout();
                          }}
                          className="absolute -right-1 -top-1 p-1 rounded bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Open in new window"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* User avatar */}
        <div className="border-t border-gray-800 p-2">
          <div className="flex flex-col items-center gap-2">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName || user.username}
                className="w-8 h-8 rounded-full"
                title={user.displayName || user.username}
              />
            ) : (
              <div
                className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center"
                title={user?.displayName || user?.username || 'Guest'}
              >
                <User className="w-4 h-4 text-gray-400" />
              </div>
            )}
            {isAuthenticated && authEnabled && (
              <button
                type="button"
                onClick={logout}
                className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </aside>
    );
  }

  // Expanded sidebar - full mode
  return (
    <aside className="flex flex-col w-64 h-full bg-gray-900 border-r border-gray-800 transition-all duration-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <button
          type="button"
          onClick={() => onViewChange(VIEWS.DASHBOARD)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <Bot className="w-6 h-6 text-indigo-400" />
          <h1 className="text-lg font-semibold text-white">Claude</h1>
        </button>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
        >
          <PanelLeftClose className="w-5 h-5" />
        </button>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Workspace Sessions List - ALWAYS visible */}
        <WorkspaceSessionsList onViewChange={onViewChange} />
      </div>

      {/* User Navigation - expanded */}
      <div className="border-t border-gray-800 p-2 space-y-1">
        <button
          type="button"
          onClick={() => onViewChange(VIEWS.USER_AGENTS)}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
            ${currentView === VIEWS.USER_AGENTS
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }
            transition-colors
          `}
        >
          <Bot className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">My Agents</span>
        </button>
        <button
          type="button"
          onClick={() => onViewChange(VIEWS.USER_PROJECTS)}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
            ${currentView === VIEWS.USER_PROJECTS
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }
            transition-colors
          `}
        >
          <FolderKanban className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">My Projects</span>
        </button>
        <button
          type="button"
          onClick={() => onViewChange(VIEWS.USER_TASKS)}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
            ${currentView === VIEWS.USER_TASKS
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }
            transition-colors
          `}
        >
          <ListTodo className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">My Tasks</span>
        </button>
        <button
          type="button"
          onClick={() => onViewChange(VIEWS.USER_TEMPLATES)}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
            ${currentView === VIEWS.USER_TEMPLATES
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }
            transition-colors
          `}
        >
          <FileText className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">My Templates</span>
          {pendingCount > 0 && (
            <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 text-xs font-bold text-orange-300 bg-orange-500/20 rounded-full px-1.5" title={`${pendingCount} pending edit${pendingCount !== 1 ? 's' : ''}`} aria-label={`${pendingCount} pending edits`}>
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Admin Panel - at bottom, above user (only for admins) */}
      {isAdmin && (
        <div className="border-t border-gray-800">
          {/* Admin Navigation Toggle */}
          <div className="p-2">
            <button
              type="button"
              onClick={() => setShowAdminNav(!showAdminNav)}
              className={`
                w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm
                ${showAdminNav || isAdminView
                  ? 'bg-indigo-600/20 text-indigo-400'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }
                transition-colors
              `}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                <span>Admin Panel</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${showAdminNav ? 'rotate-90' : ''}`} />
            </button>
          </div>

          {/* Admin Navigation Items */}
          {showAdminNav && (
            <div className="p-2 pt-0 space-y-1">
              {ADMIN_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                const isTasksItem = item.id === VIEWS.PROJECT_TASKS;
                return (
                  <div key={item.id} className="relative group flex items-center">
                    <button
                      type="button"
                      onClick={() => onViewChange(item.id)}
                      className={`
                        flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                        ${isActive
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        }
                        transition-colors
                      `}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">{item.label}</div>
                      </div>
                    </button>
                    {/* Popout button for Tasks */}
                    {isTasksItem && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTasksPopout();
                        }}
                        className="ml-1 p-1.5 rounded text-gray-500 hover:text-white hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Open in new window"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* User section */}
      <div className="border-t border-gray-800 p-3">
        <div className="flex items-center gap-3">
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName || user.username}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
              <User className="w-4 h-4 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-300 truncate">
              {user?.displayName || user?.username || (authEnabled ? 'Not logged in' : 'Guest')}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || (authEnabled ? '' : 'Auth disabled')}
            </p>
          </div>
          {isAuthenticated && authEnabled && (
            <button
              type="button"
              onClick={logout}
              className="p-1.5 rounded text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
});

/**
 * Dashboard content (home screen)
 * @returns {React.ReactElement} Dashboard content
 */
const DashboardContent = memo(function DashboardContent() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-indigo-500/25">
          <Bot className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">
          Claude Web UI
        </h2>
        <p className="text-gray-400 mb-6">
          Agent Messaging System - Start a new session or select an existing one
          to begin working with Claude.
        </p>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/25"
        >
          <MessageSquarePlus className="w-5 h-5" />
          Start New Session
        </button>
      </div>
    </div>
  );
});

/**
 * Tasks view wrapper that fetches projects and renders TasksView
 * @param {object} props - Component props
 * @param {boolean} [props.isPopout=false] - Whether this is rendered in popout mode
 * @returns {React.ReactElement} Tasks view with project selector
 */
const TasksViewWrapper = memo(function TasksViewWrapper({ isPopout = false }) {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch projects on mount
  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await fetch('/api/projects', { credentials: 'include' });
        if (response.ok) {
          const result = await response.json();
          const projectsList = result.data || result || [];
          setProjects(projectsList);
          // Auto-select first active project (isActive is a boolean, not status enum)
          const activeProject = projectsList.find(p => p.isActive && !p.isArchived);
          if (activeProject) {
            setSelectedProject(activeProject);
          }
        } else {
          setError(`API Error: ${response.status}`);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchProjects();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-2">Error loading projects</div>
          <div className="text-gray-500 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ListTodo className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <div className="text-gray-400 mb-2">No projects found</div>
          <div className="text-gray-500 text-sm">Create a project first to manage tasks</div>
        </div>
      </div>
    );
  }

  // Auto-select first project if none selected
  const activeProjects = projects.filter(p => p.isActive && !p.isArchived);
  const effectiveProject = selectedProject || activeProjects[0];

  return (
    <div className="flex-1 flex flex-col h-full">
      <TasksView
        project={effectiveProject}
        projects={activeProjects}
        onProjectChange={setSelectedProject}
        isPopout={isPopout}
      />
    </div>
  );
});

/**
 * Popout Tasks View - minimal UI for external window
 * @returns {React.ReactElement} Popout tasks view
 */
const PopoutTasksView = memo(function PopoutTasksView() {
  return (
    <div className="h-screen bg-gray-950">
      <TasksViewWrapper isPopout={true} />
    </div>
  );
});

/**
 * Sessions view wrapper that fetches agents and renders SessionsView
 * @returns {React.ReactElement} Sessions view with agents
 */
const SessionsViewWrapper = memo(function SessionsViewWrapper() {
  const [agents, setAgents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch agents on mount
  useEffect(() => {
    async function fetchAgents() {
      try {
        console.log('[SessionsView] Fetching agents...');
        const response = await fetch('/api/agents', {
          credentials: 'include',
        });
        console.log('[SessionsView] Response status:', response.status);
        if (response.ok) {
          const result = await response.json();
          console.log('[SessionsView] Agents data:', result);
          // API returns { data: [...], pagination: {...} }
          const agentsList = result.data || result.agents || result || [];
          const agentsMap = {};
          agentsList.forEach(agent => {
            agentsMap[agent.id] = agent;
          });
          console.log('[SessionsView] Agents map:', agentsMap);
          setAgents(agentsMap);
        } else {
          const errText = await response.text();
          console.error('Failed to fetch agents:', response.status, errText);
          setError(`API Error: ${response.status}`);
        }
      } catch (error) {
        console.error('Failed to fetch agents:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
    fetchAgents();
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-2">Error loading agents</div>
          <div className="text-gray-500 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  return <SessionsView agents={agents} />;
});


/**
 * Placeholder for admin views that don't have dedicated components yet
 * @param {object} props - Component props
 * @param {string} props.title - View title
 * @param {string} props.description - View description
 * @returns {React.ReactElement} Placeholder content
 */
const AdminPlaceholder = memo(function AdminPlaceholder({ title, description }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 rounded-2xl bg-gray-800 flex items-center justify-center mx-auto mb-6">
          <Settings className="w-8 h-8 text-gray-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">{title}</h2>
        <p className="text-gray-400">{description}</p>
      </div>
    </div>
  );
});

/**
 * Main content area with view routing
 * @param {object} props - Component props
 * @param {string} props.currentView - Current active view
 * @param {Function} props.onViewChange - View change callback
 * @returns {React.ReactElement} Main content
 */
const MainContent = memo(function MainContent({ currentView, onViewChange }) {
  // Get current view title for breadcrumb
  const currentNavItem = ADMIN_NAV_ITEMS.find(item => item.id === currentView);
  const viewTitle = currentNavItem?.label || 'Dashboard';
  const isAdminView = currentView?.startsWith('admin:');

  // Render content based on current view
  const renderContent = () => {
    switch (currentView) {
      case VIEWS.SESSIONS:
        return <SessionsViewWrapper />;
      case VIEWS.USER_AGENTS:
        return <AgentsView adminMode={false} />;
      case VIEWS.USER_PROJECTS:
        return <UserProjectsView />;
      case VIEWS.USER_TASKS:
        return <UserTasksView />;
      case VIEWS.USER_TEMPLATES:
        return <ClaudeMdManager />;
      case VIEWS.PROJECT_TASKS:
        return <TasksViewWrapper />;
      case VIEWS.ADMIN_AGENTS:
        return <AgentsView adminMode={true} />;
      case VIEWS.ADMIN_HOSTS:
        return <HostManager />;
      case VIEWS.ADMIN_CHANNELS:
        return <ChannelManager />;
      case VIEWS.ADMIN_ROLES:
        return <RoleManager />;
      case VIEWS.ADMIN_SKILLS:
        return <SkillManager />;
      case VIEWS.ADMIN_TEMPLATES:
        return <ClaudeMdManager />;
      case VIEWS.ADMIN_PROJECTS:
        return <ProjectManager />;
      case VIEWS.ADMIN_USERS:
        return <UserManager />;
      case VIEWS.ADMIN_INSIGHT_CACHE:
        return <InsightCacheConfig />;
      case VIEWS.ADMIN_WHISPERER:
        return <WhispererConfig />;
      case VIEWS.ADMIN_SYSTEM_PROMPTS:
        return <SystemPromptEditor />;
      case VIEWS.ADMIN_CREDENTIALS:
        return <CredentialManager />;
      case VIEWS.DASHBOARD:
      default:
        return <DashboardContent />;
    }
  };

  return (
    <main className="flex-1 flex flex-col bg-gray-900 overflow-hidden">
      {/* Admin breadcrumb header */}
      {isAdminView && (
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-800">
          <button
            type="button"
            onClick={() => onViewChange(VIEWS.DASHBOARD)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Admin</span>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">{viewTitle}</span>
          </div>
        </div>
      )}

      {/* Content - views with internal scroll (e.g. ClaudeMdManager) need overflow-hidden */}
      <div className={`flex-1 ${currentView === VIEWS.USER_TEMPLATES || currentView === VIEWS.ADMIN_TEMPLATES ? 'overflow-hidden' : 'overflow-auto'}`}>
        {renderContent()}
      </div>
    </main>
  );
});

/**
 * Chat panel with dropdown header for channel/DM selection
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether panel is open
 * @param {Function} props.onClose - Close callback
 * @returns {React.ReactElement} Chat panel
 */
const ChatPanel = memo(function ChatPanel({
  isOpen,
  onClose
}) {
  const {
    activeChannel,
    activeDmRecipient,
    createChannel,
    selectChannel,
    agents
  } = useChat();

  // Animation state
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimer = useRef(null);

  // Handle open/close animation
  useEffect(() => {
    if (animationTimer.current) {
      clearTimeout(animationTimer.current);
    }

    if (isOpen) {
      // Opening: render first, then animate in
      setShouldRender(true);
      // Use requestAnimationFrame to ensure DOM is ready before starting animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Closing: animate out, then unmount
      setIsAnimating(false);
      animationTimer.current = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match animation duration
    }

    return () => {
      if (animationTimer.current) {
        clearTimeout(animationTimer.current);
      }
    };
  }, [isOpen]);

  // Reply state for ChatInput
  const [replyTo, setReplyTo] = useState(null);

  // Channel creation modal state
  const [showChannelForm, setShowChannelForm] = useState(false);

  // Handle cancel reply
  const handleCancelReply = useCallback(() => {
    setReplyTo(null);
  }, []);

  // Handle message click for reply
  const handleMessageClick = useCallback((message) => {
    setReplyTo(message);
  }, []);

  // Handle create channel button click
  const handleCreateChannelClick = useCallback(() => {
    setShowChannelForm(true);
  }, []);

  // Handle channel form submit
  const handleChannelFormSubmit = useCallback(async (channelData) => {
    const newChannel = await createChannel(channelData);
    setShowChannelForm(false);
    // Select the newly created channel
    if (newChannel) {
      selectChannel(newChannel);
    }
  }, [createChannel, selectChannel]);

  // Handle channel form cancel
  const handleChannelFormCancel = useCallback(() => {
    setShowChannelForm(false);
  }, []);

  // Don't render if animation is complete and panel should be hidden
  if (!shouldRender) return null;

  // Determine if we have an active conversation
  const hasActiveConversation = Boolean(activeChannel || activeDmRecipient);

  return (
    <div
      className={`
        flex flex-col h-full bg-gray-900 border-l border-gray-700
        transform transition-transform duration-300 ease-out
        ${isAnimating ? 'translate-x-0' : 'translate-x-full'}
      `}
      style={{ width: CHAT_PANEL_WIDTH }}
    >
      {/* Channel Creation Modal */}
      {showChannelForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <ChannelForm
              onSubmit={handleChannelFormSubmit}
              onCancel={handleChannelFormCancel}
            />
          </div>
        </div>
      )}

      {/* Dropdown Header for channel/DM selection */}
      <ChatChannelDropdown
        onCreateChannel={handleCreateChannelClick}
        onClose={onClose}
      />

      {/* Message Area */}
      {hasActiveConversation ? (
        <>
          {/* Message Window */}
          <ChatWindow
            className="flex-1 min-h-0"
            onMessageClick={handleMessageClick}
          />

          {/* Input Area */}
          <ChatInput
            replyTo={replyTo}
            onCancelReply={handleCancelReply}
            autoFocus
            agents={agents}
          />
        </>
      ) : (
        /* Empty state when no conversation selected */
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-900">
          <div className="text-center px-4">
            <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              Select a conversation
            </h3>
            <p className="text-sm text-gray-500 max-w-xs">
              Use the dropdown above to choose a channel or start a direct message.
            </p>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * App header with navigation and chat toggle
 * @param {object} props - Component props
 * @param {boolean} props.isChatOpen - Whether chat is open
 * @param {Function} props.onChatToggle - Toggle callback
 * @param {number} props.unreadCount - Unread message count
 * @param {string} props.currentView - Current view
 * @param {Function} props.onViewChange - View change callback
 * @returns {React.ReactElement} App header
 */
const AppHeader = memo(function AppHeader({ isChatOpen, onChatToggle, unreadCount, currentView, onViewChange }) {
  const currentNavItem = ADMIN_NAV_ITEMS.find(item => item.id === currentView);
  const isAdminView = currentView?.startsWith('admin:');

  return (
    <header className="flex items-center justify-between h-14 px-4 bg-gray-950 border-b border-gray-800">
      {/* Left: Navigation breadcrumb */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onViewChange(VIEWS.DASHBOARD)}
          className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          title="Home"
        >
          <Home className="w-5 h-5" />
        </button>
        <span className="text-gray-600">/</span>
        {isAdminView ? (
          <>
            <span className="text-sm text-gray-500">Admin</span>
            <span className="text-gray-600">/</span>
            <span className="text-sm text-gray-300">{currentNavItem?.label}</span>
          </>
        ) : (
          <span className="text-sm text-gray-400">Dashboard</span>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        <ChatToggleButton
          isChatOpen={isChatOpen}
          onToggle={onChatToggle}
          unreadCount={unreadCount}
        />
      </div>
    </header>
  );
});

// ============================================
// Main App Content (with ChatContext access)
// ============================================

/**
 * App content component that has access to ChatContext
 * @returns {React.ReactElement} App content
 */
function AppContent() {
  // Current view state
  const [currentView, setCurrentView] = useState(VIEWS.DASHBOARD);

  // UI state from Zustand store (persisted automatically)
  const isChatOpen = useUIStore((state) => state.chatPanelVisible);
  const isSidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);
  const toggleChatPanel = useUIStore((state) => state.toggleChatPanel);
  const setChatPanelVisible = useUIStore((state) => state.setChatPanelVisible);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);

  // Get chat context for channel sync
  const { totalUnreadCount, activeChannel, selectChannel, channels } = useChat();

  // Get workspace session context for channel persistence
  const { activeSession, queueAutoSave } = useWorkspaceSession();

  // Extract primitives for stable dependencies
  const activeSessionId = activeSession?.id;
  const savedChannelId = activeSession?.activeChannelId;

  // Track if we're loading channel from session (to prevent save loop)
  const isLoadingChannelRef = useRef(false);
  const lastSessionIdRef = useRef(null);

  // Sync channel from session on session change
  useEffect(() => {
    if (!activeSessionId) return;

    // Only load if session changed
    if (lastSessionIdRef.current === activeSessionId) return;
    lastSessionIdRef.current = activeSessionId;

    // If session has a saved channel, select it
    if (savedChannelId && channels?.length > 0) {
      const channel = channels.find(c => c.id === savedChannelId);
      if (channel) {
        isLoadingChannelRef.current = true;
        selectChannel(channel);
        // Clear flag after React processes the update
        setTimeout(() => {
          isLoadingChannelRef.current = false;
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, channels]);

  // Save channel to session when it changes
  useEffect(() => {
    // Skip if loading from session or no session active
    if (isLoadingChannelRef.current) return;
    if (!activeSessionId) return;
    if (!queueAutoSave) return;

    // Only save if channel is different from saved
    const newChannelId = activeChannel?.id || null;
    if (newChannelId !== savedChannelId) {
      queueAutoSave({ activeChannelId: newChannelId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel?.id, activeSessionId, savedChannelId]);

  // Handle view change
  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Handle chat panel close
  const handleChatClose = useCallback(() => {
    setChatPanelVisible(false);
  }, [setChatPanelVisible]);

  return (
    <div className="flex flex-col h-screen bg-gray-950">
      {/* Top header bar */}
      <AppHeader
        isChatOpen={isChatOpen}
        onChatToggle={toggleChatPanel}
        unreadCount={totalUnreadCount}
        currentView={currentView}
        onViewChange={handleViewChange}
      />

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Claude sessions & Admin nav */}
        <LeftSidebar
          currentView={currentView}
          onViewChange={handleViewChange}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={toggleSidebar}
        />

        {/* Center content area */}
        <MainContent
          currentView={currentView}
          onViewChange={handleViewChange}
        />

        {/* Right sidebar - Chat panel */}
        <ChatPanel
          isOpen={isChatOpen}
          onClose={handleChatClose}
        />
      </div>
    </div>
  );
}

// ============================================
// Login Screen
// ============================================

/**
 * Login screen shown when user is not authenticated
 * @returns {React.ReactElement} Login screen
 */
const LoginScreen = memo(function LoginScreen() {
  const { login } = useAuth();

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-950">
      <div className="text-center max-w-md px-4">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-8 shadow-lg shadow-indigo-500/25">
          <Bot className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">
          Claude Web UI
        </h1>
        <p className="text-gray-400 mb-8">
          Agent Messaging System - Sign in to continue
        </p>
        <button
          type="button"
          onClick={() => login('/')}
          className="inline-flex items-center gap-2 px-8 py-3 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/25"
        >
          <LogOut className="w-5 h-5 rotate-180" />
          Sign in with SSO
        </button>
      </div>
    </div>
  );
});

/**
 * Loading screen shown during auth check
 * @returns {React.ReactElement} Loading screen
 */
const LoadingScreen = memo(function LoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-950">
      <div className="text-center">
        <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Loading...</p>
      </div>
    </div>
  );
});

// ============================================
// Auth-Aware App Wrapper
// ============================================

/**
 * Check if we're in popout mode based on URL
 * @returns {boolean} Whether popout mode is active
 */
function isPopoutMode() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('popout') === 'true' && window.location.pathname === '/tasks';
}

/**
 * App wrapper that handles auth state and conditionally renders content
 * @returns {React.ReactElement} App wrapper
 */
function AuthenticatedApp() {
  const { user, loading, isAuthenticated, authEnabled } = useAuth();
  const isPopout = isPopoutMode();

  // Show loading screen while checking auth
  if (loading) {
    return <LoadingScreen />;
  }

  // If auth is enabled and user is not authenticated, show login
  if (authEnabled && !isAuthenticated) {
    return <LoginScreen />;
  }

  // Popout mode - render minimal tasks view without full app chrome
  if (isPopout) {
    return (
      <MessagesProvider>
        <UIProvider>
          <PopoutTasksView />
        </UIProvider>
      </MessagesProvider>
    );
  }

  // User is authenticated (or auth is disabled), render the app
  // Key on user.id ensures all contexts are reset when user changes (login/logout)
  // This provides automatic cleanup of user-specific data on logout
  const userKey = user?.id || 'guest';
  return (
    <MessagesProvider key={`messages-${userKey}`}>
      <WorkspaceSessionProvider key={`workspace-${userKey}`}>
        <UIProvider key={`ui-${userKey}`}>
          <AgentChatProvider key={`agentchat-${userKey}`}>
            <ChatProvider userId={user?.id || 'guest'} agentId={null} agentUuid={null} key={`chat-${userKey}`}>
              <AppContent />
            </ChatProvider>
          </AgentChatProvider>
        </UIProvider>
      </WorkspaceSessionProvider>
    </MessagesProvider>
  );
}

// ============================================
// Main App Component
// ============================================

/**
 * Main App Component
 * Wraps the application with AuthProvider and ChatProvider for state management.
 * @returns {React.ReactElement} App component
 */
function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;

// ============================================
// Named Exports
// ============================================

export {
  ChatToggleButton,
  LeftSidebar,
  MainContent,
  DashboardContent,
  SessionsViewWrapper,
  TasksViewWrapper,
  PopoutTasksView,
  AdminPlaceholder,
  ChatPanel,
  AppHeader,
  AppContent,
  LoginScreen,
  LoadingScreen,
  AuthenticatedApp,
  CHAT_PANEL_WIDTH,
  VIEWS,
  ADMIN_NAV_ITEMS,
  isPopoutMode
};
